import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

export type QuickFillAutoFillPreviewMode = 'all' | 'selected';

export interface QuickFillAutoFillPreview {
  matches: number;
  total: number;
  preview: string;
}

export interface QuickFillAutoFillPreviewDialogProps {
  open: boolean;
  previewOnly: boolean;
  mode: QuickFillAutoFillPreviewMode;
  preview: QuickFillAutoFillPreview;
  contentRef?: React.Ref<HTMLDivElement>;
  onClose: () => void;
  onApply: () => void;
}

export const QuickFillAutoFillPreviewDialog: React.FC<QuickFillAutoFillPreviewDialogProps> = ({
  open,
  previewOnly,
  mode,
  preview,
  contentRef,
  onClose,
  onApply,
}) => {
  const theme = useTheme();
  const handleClose = () => {
    if (previewOnly) {
      return;
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="auto-fill-preview-title"
    >
      <DialogTitle id="auto-fill-preview-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoFixHighIcon color="secondary" />
        Auto-fill preview
      </DialogTitle>
      <DialogContent ref={contentRef} data-testid="quickfill-tour-target-auto-fill-preview">
        <DialogContentText component="div">
          <Typography variant="body1" paragraph>{preview.preview}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Suggested matches: {preview.matches} of {preview.total} rows
            {mode === 'selected' ? ' in the current selection.' : ' in the current table.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>Auto-fill analyzes family names to detect:</Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li><Typography variant="body2"><strong>OBJECT-TYPE</strong> — Component category (ELBOW, VALVE, TEE, etc.)</Typography></li>
            <li><Typography variant="body2"><strong>SKEY</strong> — Symbol key with angle (EL90, VB01, etc.)</Typography></li>
            <li><Typography variant="body2"><strong>End types</strong> — Connection methods (BW, SW, FL, SC, etc.)</Typography></li>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Supports multiple languages: English, German, Russian, Spanish, French, and Chinese keywords.
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
            Note: Auto-fill adjustments are direct Quick Fill repairs, not exclusions. Existing editable values will be replaced with recognized values.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {previewOnly ? (
          <>
            <Button disabled color="inherit" aria-label="Cancel auto-fill">Cancel</Button>
            <Button disabled color="secondary" variant="contained" aria-label="Apply auto-fill changes" startIcon={<AutoFixHighIcon />} autoFocus>
              Apply auto-fill
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} color="inherit" aria-label="Cancel auto-fill">Cancel</Button>
            <Button
              onClick={onApply}
              color="secondary"
              variant="contained"
              aria-label="Apply auto-fill changes"
              startIcon={<AutoFixHighIcon />}
              autoFocus
              data-attention="true"
              sx={{
                boxShadow: `0 0 0 2px ${alpha(theme.palette.secondary.main, 0.22)}, 0 0 16px ${alpha(theme.palette.secondary.main, 0.38)}`,
                '&:hover': {
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.secondary.main, 0.3)}, 0 0 20px ${alpha(theme.palette.secondary.main, 0.46)}`,
                },
              }}
            >
              Apply auto-fill
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
